import { useEffect, useRef } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import { Flex, Card, Text, Button } from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import styles from './SavedRepliesPopover.module.css'

/**
 * Colocated data contract for a single saved-reply row (shared across the
 * SavedRepliesPopover subtree: the popover itself and the keyboard-nav hook).
 *
 * Selects the full set of required `SavedReply` fields rather than only the
 * title/content this row renders: the row forwards the value to the
 * `handleInsertSavedReply` callback, which is still typed against the broad
 * codegen `SavedReply` upstream (in `useEngagementReplyDraft`), so the fragment
 * must remain assignable to it while that producer/consumer stays unmigrated.
 */
export const SavedReplyItem_SavedReply = graphql(/* GraphQL */ `
  fragment SavedReplyItem_SavedReply on SavedReply @_unmask {
    id
    organizationId
    title
    content {
      text
    }
    createdAt
    updatedAt
  }
`)

export const SavedReplyItem = ({
  savedReply,
  handleInsertSavedReply,
  isSelected,
  onMouseEnter,
  onMouseLeave,
}: {
  savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>
  handleInsertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void
  isSelected: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
}): JSX.Element => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const savedReplyData = readFragment(SavedReplyItem_SavedReply, savedReply)

  useEffect(() => {
    if (isSelected && buttonRef.current) {
      buttonRef.current.focus()
    }
  }, [isSelected])

  return (
    // oxlint-disable-next-line jsx-a11y/interactive-supports-focus -- The parent listbox tracks the active option while focus moves to this row's Insert action.
    <Card
      padding="sm"
      className={clsx(styles.savedReplyItem, isSelected && styles.isSelected)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The parent listbox tracks the active option while focus moves to this row's Insert action.
      role="option"
      aria-selected={isSelected}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Flex
        direction="row"
        justify="between"
        align="center"
        gap="2xs"
        fullHeight
      >
        <Flex
          direction="column"
          gap="2xs"
          align="center"
          className={styles.savedReplyItemContent}
        >
          {savedReplyData?.title && (
            <Text truncate className={styles.title}>
              {savedReplyData?.title}
            </Text>
          )}
          <Text truncate className={styles.content}>
            {savedReplyData?.content?.text}
          </Text>
        </Flex>
        <Button
          variant="primary"
          size="medium"
          className={styles.insertButton}
          ref={buttonRef}
          onClick={(): void => {
            handleInsertSavedReply(savedReply)
          }}
        >
          Insert
        </Button>
      </Flex>
    </Card>
  )
}
